import { FC, useCallback, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { format } from 'date-fns';
import { Software } from 'models';
import * as routes from 'router/Routes';

import Card from 'components/UI/Card';
import CardHeader from 'components/UI/Card/CardHeader';
import { CardHeaderTop, CardHeaderTopColumn } from 'pages/Software/styled';
import InfoItem from 'components/UI/InfoItem';
import { ContentContainer, NameContainer, DetailGroup } from './styled';
import Loader from 'components/UI/Loader';
import Button from 'components/UI/Button';
import { useAccess } from 'hooks/useAccess';
import Heading, { Type as HeadingType } from 'components/UI/Heading';
import { getSoftwareById } from 'services/softwareService/softwareService';
import EditSoftwareModal from '../EditSoftwareModal';

type Props = {
  software: Software | null;
  isLoading: boolean;
};

const SoftwareInfoCard: FC<Props> = ({ software, isLoading }) => {
  const intl = useIntl();
  const { softwareUploadAccess } = useAccess();

  const [openEditModal, setOpenEditModal] = useState<boolean>(false);

  const onEdit = useCallback(() => {
    setOpenEditModal(true);
  }, [setOpenEditModal]);

  const onClose = useCallback(() => {
    setOpenEditModal(false);
  }, [setOpenEditModal]);

  const onDownload = useCallback(() => {
    if (software) {
      getSoftwareById(software.id, true).then((data) => {
        if (data?.attributes?.file?.preSignedUrl) {
          const url = data.attributes.file.preSignedUrl;
          const fileName = data.attributes.file.fileName;
          const a = document.createElement('a');
          a.href = url;
          a.download = fileName;
          document.body.appendChild(a);
          a.click();
          a.remove();
        }
      });
    }
  }, [software]);

  // Details
  const softwareDetails = useMemo(() => {
    if (isLoading) {
      return <Loader center />;
    }
    if (!software) {
      return null;
    }
    const {
      file,
      pnc,
      brand,
      bundleId,
      category,
      firmwareVersion,
      createdAt,
      mimetype,
      purposes,
      releaseStage,
      revision,
    } = software.attributes;
    // Detail list
    const softwareList = [
      {
        id: 1,
        title: 'software.pnc',
        text: pnc,
        conditional: false,
      },
      {
        id: 2,
        title: 'software.revision',
        text: revision,
        conditional: false,
      },
      {
        id: 3,
        title: 'software.brand',
        text: brand,
        conditional: false,
      },
      {
        id: 4,
        title: 'software.category',
        text: category,
        conditional: false,
      },
      {
        id: 5,
        title: 'software.purposes',
        text: purposes,
        conditional: false,
      },
      {
        id: 6,
        title: 'software.created_date',
        text: createdAt
          ? format(Number(createdAt), 'yyyy-MM-dd HH:mm:ss')
          : '-',
        conditional: false,
      },
      {
        id: 7,
        title: 'software.firmware_version',
        text: firmwareVersion,
        conditional: false,
      },
      {
        id: 8,
        title: 'software.bundle_id',
        text: bundleId,
        conditional: false,
      },
      {
        id: 9,
        title: 'software.mimetype',
        text: mimetype,
        conditional: false,
      },
      {
        id: 10,
        title: 'software.file_name',
        text: file.fileName,
        conditional: false,
      },
      {
        id: 11,
        title: 'software.released_stage',
        text: releaseStage,
        conditional: false,
      },
    ];

    const softwareAttributes = softwareList.map(
      ({ id, title, text, conditional }) => {
        if (!text && conditional) {
          return null;
        }
        return (
          <InfoItem
            key={id}
            title={intl.formatMessage({ id: title })}
            value={text || '–'}
          />
        );
      }
    );
    return (
      <div>
        <CardHeaderTop>
          <CardHeaderTopColumn>
            <Heading type={HeadingType.h3} uppercase>
              {`${software?.attributes.name} ${software?.attributes.version}`}
            </Heading>
          </CardHeaderTopColumn>
          <CardHeaderTopColumn>
            {softwareUploadAccess && (
              <div>
                <Button
                  backgroundColor="transparent"
                  color="primary"
                  onClick={onEdit}
                >
                  <FormattedMessage id="software.btn_edit" />
                </Button>
                <Button
                  id="download_software_btn"
                  backgroundColor="transparent"
                  color="primary"
                  onClick={onDownload}
                >
                  <FormattedMessage id="software.btn_download" />
                </Button>
              </div>
            )}
          </CardHeaderTopColumn>
        </CardHeaderTop>
        <ContentContainer>
          <NameContainer>
            <DetailGroup>{softwareAttributes}</DetailGroup>
          </NameContainer>
        </ContentContainer>
      </div>
    );
  }, [intl, software, isLoading, onDownload, onEdit, softwareUploadAccess]);

  return (
    <Card>
      <CardHeader navUrl={routes.SOFTWARES} title="software.btn_back" />
      {softwareDetails}
      <EditSoftwareModal
        open={openEditModal}
        onClose={onClose}
        software={software}
      />
    </Card>
  );
};

export default SoftwareInfoCard;
